// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: one section of the Monday Madness editor. Text sections are a paragraph, task lists
// are rows with a link and hours, time tables are rows with hours and a bar. Enter adds a
// row, Backspace on an empty row removes it, and pasting several lines makes several rows.

import {useMondayChoices} from '@app/features/konvo_reports/components/MondayChoiceMenu';
import {MondayMarkdownField} from '@app/features/konvo_reports/components/MondayMarkdownField';
import {newItem, parseHours, removeAt, replaceAt} from '@app/features/konvo_reports/utils/MondayDraftEdit';
import {ClickUpLogo} from '@app/features/ui/components/icons/ClickUpLogo';
import {IconButton} from '@app/features/ui/konvo_controls/KonvoControls';
import {
	formatMinutes,
	type MondayDraftItem,
	type MondayDraftSection,
	type MondayItemState,
	sectionMinutes,
} from '@fluxer/schema/src/domains/konvo/MondayReportDraft';
import {useLingui} from '@lingui/react/macro';
import {
	IconAlignLeft,
	IconArrowDown,
	IconArrowUp,
	IconCheck,
	IconClockHour4,
	IconEye,
	IconEyeOff,
	IconLink,
	IconListCheck,
	IconPlus,
	IconProgress,
	IconTrash,
	IconX,
} from '@tabler/icons-react';
import {clsx} from 'clsx';
import type React from 'react';
import {useEffect, useRef, useState} from 'react';
import styles from './MondayReportCard.module.css';

const ICON = {size: 16, stroke: 1.75} as const;

/**
 * Row buttons only show while the row has focus on a touch screen. Pressing one must not take
 * focus from the row's input, or the button hides before the tap lands.
 */
const keepRowFocus = (event: React.MouseEvent) => event.preventDefault();

/** Hours as the user types them ("1.5", "1h 30m", "1:30"); stored as minutes when the field is left. */
function HoursInput({
	minutes,
	onChange,
	label,
}: {
	minutes: number | null;
	onChange: (minutes: number | null) => void;
	label: string;
}) {
	const shown = minutes ? formatMinutes(minutes) : '';
	const [text, setText] = useState(shown);
	const [editing, setEditing] = useState(false);
	useEffect(() => {
		if (!editing) setText(shown);
	}, [shown, editing]);
	const parsed = parseHours(text);
	return (
		<input
			className={clsx(styles.hours, parsed === undefined && styles.invalid)}
			aria-label={label}
			title={label}
			inputMode="decimal"
			placeholder="0h"
			value={text}
			maxLength={12}
			onFocus={() => setEditing(true)}
			onChange={(event) => {
				setText(event.target.value);
				const next = parseHours(event.target.value);
				if (next !== undefined) onChange(next);
			}}
			onBlur={() => {
				setEditing(false);
				setText(parsed === undefined ? shown : parsed ? formatMinutes(parsed) : '');
			}}
		/>
	);
}

function LinkField({url, onChange}: {url: string | null; onChange: (url: string | null) => void}) {
	const {t} = useLingui();
	const [text, setText] = useState(url ?? '');
	const valid = !text.trim() || /^https?:\/\/\S+$/i.test(text.trim());
	return (
		<input
			className={clsx(styles.linkField, !valid && styles.invalid)}
			aria-label={t`Link`}
			placeholder="https://"
			value={text}
			maxLength={2000}
			// biome-ignore lint/a11y/noAutofocus: opened on purpose from the link button
			autoFocus
			onChange={(event) => {
				setText(event.target.value);
				const next = event.target.value.trim();
				if (!next) onChange(null);
				else if (/^https?:\/\/\S+$/i.test(next)) onChange(next);
			}}
		/>
	);
}

export interface SectionMoves {
	index: number;
	count: number;
	onMove: (delta: number) => void;
}

/** Up, down, hide and remove, shown when the pointer or focus is on the block. */
export function BlockActions({
	moves,
	hidden,
	onToggleHidden,
	onRemove,
	what,
}: {
	moves: SectionMoves;
	hidden: boolean;
	onToggleHidden: () => void;
	onRemove: () => void;
	what: 'section' | 'project';
}) {
	const {t} = useLingui();
	return (
		<div className={styles.blockActions}>
			<IconButton
				className={styles.tinyIcon}
				label={what === 'project' ? t`Move project up` : t`Move section up`}
				icon={<IconArrowUp {...ICON} />}
				disabled={moves.index === 0}
				onClick={() => moves.onMove(-1)}
			/>
			<IconButton
				className={styles.tinyIcon}
				label={what === 'project' ? t`Move project down` : t`Move section down`}
				icon={<IconArrowDown {...ICON} />}
				disabled={moves.index >= moves.count - 1}
				onClick={() => moves.onMove(1)}
			/>
			<IconButton
				className={styles.tinyIcon}
				label={hidden ? t`Show in the email` : t`Hide from the email`}
				icon={hidden ? <IconEyeOff {...ICON} /> : <IconEye {...ICON} />}
				active={hidden}
				onClick={onToggleHidden}
			/>
			<IconButton
				className={styles.tinyIcon}
				label={what === 'project' ? t`Remove project` : t`Remove section`}
				icon={<IconTrash {...ICON} />}
				onClick={onRemove}
			/>
		</div>
	);
}

export function SectionKindIcon({kind}: {kind: MondayDraftSection['kind']}) {
	if (kind === 'list') return <IconListCheck {...ICON} aria-hidden="true" />;
	if (kind === 'time') return <IconClockHour4 {...ICON} aria-hidden="true" />;
	return <IconAlignLeft {...ICON} aria-hidden="true" />;
}

function useStateLabels(): Record<MondayItemState, string> {
	const {t} = useLingui();
	return {done: t`Done`, progress: t`In progress`, hold: t`On hold`};
}

function ItemRows({
	section,
	onChange,
	showHours,
}: {
	section: MondayDraftSection;
	onChange: (section: MondayDraftSection) => void;
	showHours: boolean;
}) {
	const {t} = useLingui();
	const stateLabels = useStateLabels();
	const stateMenu = useMondayChoices();
	const inputs = useRef(new Map<string, HTMLInputElement>());
	const [focusId, setFocusId] = useState<string | null>(null);
	const [linkId, setLinkId] = useState<string | null>(null);
	useEffect(() => {
		if (!focusId) return;
		inputs.current.get(focusId)?.focus();
		setFocusId(null);
	}, [focusId]);
	const time = section.kind === 'time';
	const most = Math.max(1, ...section.items.map((item) => item.minutes ?? 0));
	const setItems = (items: Array<MondayDraftItem>) => onChange({...section, items});
	const change = (index: number, item: MondayDraftItem) => setItems(replaceAt(section.items, index, item));
	const insertAfter = (index: number, texts: Array<string> = ['']) => {
		const added = texts.map((text) => newItem({text: text.slice(0, 500)}));
		const items = [...section.items];
		items.splice(index + 1, 0, ...added);
		setItems(items.slice(0, 80));
		setFocusId(added[added.length - 1].id);
	};
	return (
		<>
			<ul className={styles.items}>
				{section.items.map((item, index) => {
					const clickup = item.url?.includes('clickup.com') ?? false;
					const done = item.state === 'done';
					const pickState = (event: React.MouseEvent<HTMLButtonElement>) =>
						stateMenu.show(
							event,
							item.text || t`Item`,
							([null, 'done', 'progress', 'hold'] as const).map((state) => ({
								key: state ?? 'none',
								label: state ? stateLabels[state] : t`No status`,
								selected: (item.state ?? null) === state,
								onPick: () => change(index, {...item, state}),
							})),
						);
					return (
						<li key={item.id} className={styles.itemRow}>
							<div className={styles.itemLine}>
								{!time && (
									<button
										type="button"
										role="checkbox"
										aria-checked={done}
										aria-label={item.text || t`Item`}
										title={done ? t`Mark as not done` : t`Mark as done`}
										className={styles.tick}
										data-state={item.state ?? undefined}
										onClick={() => change(index, {...item, state: done ? null : 'done'})}
									>
										<span className={styles.tickBox} data-status={section.status ?? undefined} aria-hidden="true">
											{done && <IconCheck size={11} stroke={3} />}
										</span>
									</button>
								)}
								<input
									ref={(element) => {
										if (element) inputs.current.set(item.id, element);
										else inputs.current.delete(item.id);
									}}
									className={clsx(styles.itemText, done && styles.itemDone)}
									aria-label={time ? t`Row label` : t`Item`}
									placeholder={time ? t`Task or day` : t`Add an item`}
									value={item.text}
									maxLength={500}
									onChange={(event) => change(index, {...item, text: event.target.value})}
									onPaste={(event) => {
										const lines = event.clipboardData
											.getData('text')
											.split(/\r?\n/)
											.map((line) => line.replace(/^\s*(?:[-*•]|\d+[.)])\s+/, '').trim())
											.filter(Boolean);
										if (lines.length < 2) return;
										event.preventDefault();
										change(index, {...item, text: `${item.text}${lines[0]}`.slice(0, 500)});
										insertAfter(index, lines.slice(1));
									}}
									onKeyDown={(event) => {
										if (!time && event.key === 'Enter' && (event.metaKey || event.ctrlKey)) {
											event.preventDefault();
											change(index, {...item, state: done ? null : 'done'});
										} else if (event.key === 'Enter' && !event.nativeEvent.isComposing) {
											event.preventDefault();
											insertAfter(index);
										} else if (event.key === 'Backspace' && item.text === '' && section.items.length > 1) {
											event.preventDefault();
											setItems(removeAt(section.items, index));
											setFocusId(section.items[index === 0 ? 1 : index - 1].id);
										}
									}}
								/>
								{!time &&
									(item.state === 'progress' || item.state === 'hold' ? (
										<button
											type="button"
											className={styles.statePill}
											data-state={item.state}
											title={t`Change status`}
											onClick={pickState}
										>
											{stateLabels[item.state]}
										</button>
									) : (
										<IconButton
											className={clsx(styles.tinyIcon, styles.revealOnHover)}
											label={t`Set status`}
											onMouseDown={keepRowFocus}
											icon={<IconProgress {...ICON} />}
											onClick={pickState}
										/>
									))}
								{item.note && <span className={styles.itemNote}>{item.note}</span>}
								{time && (
									<span className={styles.bar} aria-hidden="true">
										<span style={{width: `${Math.max(2, ((item.minutes ?? 0) / most) * 100)}%`}} />
									</span>
								)}
								<IconButton
									className={clsx(styles.tinyIcon, !item.url && styles.revealOnHover)}
									label={item.url ? t`Edit link` : t`Add link`}
									onMouseDown={keepRowFocus}
									icon={clickup ? <ClickUpLogo size={14} /> : <IconLink {...ICON} />}
									active={linkId === item.id}
									onClick={() => setLinkId(linkId === item.id ? null : item.id)}
								/>
								{(time || showHours) && (
									<HoursInput
										label={t`Hours`}
										minutes={item.minutes}
										onChange={(minutes) => change(index, {...item, minutes})}
									/>
								)}
								<IconButton
									className={clsx(styles.tinyIcon, styles.revealOnHover)}
									label={t`Remove item`}
									onMouseDown={keepRowFocus}
									icon={<IconX {...ICON} />}
									onClick={() => setItems(removeAt(section.items, index))}
								/>
							</div>
							{linkId === item.id && <LinkField url={item.url} onChange={(url) => change(index, {...item, url})} />}
						</li>
					);
				})}
			</ul>
			{stateMenu.sheet}
			<div className={styles.itemsFooter}>
				<button
					type="button"
					className={styles.addInline}
					disabled={section.items.length >= 80}
					onClick={() => insertAfter(section.items.length - 1)}
				>
					<IconPlus size={14} stroke={1.75} aria-hidden="true" />
					{time ? t`Add row` : t`Add item`}
				</button>
				{time && section.items.length > 0 && (
					<span className={styles.total}>
						{t`Total`} <strong>{formatMinutes(sectionMinutes(section))}</strong>
					</span>
				)}
			</div>
		</>
	);
}

export function MondaySectionBlock({
	section,
	onChange,
	onRemove,
	moves,
	level,
	showHours,
}: {
	section: MondayDraftSection;
	onChange: (section: MondayDraftSection) => void;
	onRemove: () => void;
	moves: SectionMoves;
	level: 'report' | 'project';
	showHours: boolean;
}) {
	const {t} = useLingui();
	const count = section.items.filter((item) => item.text.trim()).length;
	return (
		<section
			className={clsx(styles.block, level === 'project' ? styles.subBlock : styles.reportBlock)}
			data-hidden={section.hidden || undefined}
			data-flx="konvo-monday.section"
		>
			<div className={styles.blockHeading}>
				<span className={styles.kindIcon}>
					<SectionKindIcon kind={section.kind} />
				</span>
				<input
					className={level === 'project' ? styles.subTitle : styles.sectionTitle}
					aria-label={t`Section title`}
					value={section.title}
					maxLength={160}
					onChange={(event) => onChange({...section, title: event.target.value})}
				/>
				{section.hidden && <span className={styles.hiddenTag}>{t`Hidden`}</span>}
				{section.kind === 'list' && count > 0 && <span className={styles.count}>{count}</span>}
				<BlockActions
					moves={moves}
					hidden={section.hidden}
					onToggleHidden={() => onChange({...section, hidden: !section.hidden})}
					onRemove={onRemove}
					what="section"
				/>
			</div>
			{section.kind === 'text' ? (
				<MondayMarkdownField
					className={styles.paragraph}
					label={section.title || t`Section text`}
					value={section.body}
					maxLength={12000}
					placeholder={
						level === 'project'
							? t`Add a few lines (optional)`
							: t`Write a few lines. Markdown works: # heading, - [ ] to-do, **bold**.`
					}
					onChange={(body) => onChange({...section, body})}
				/>
			) : (
				<ItemRows section={section} onChange={onChange} showHours={showHours} />
			)}
		</section>
	);
}
